<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ApiImage } from '@/types/images';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { buildSrcSet, isOptimisedImage } from '@/types/images';
import { onOfflineMediaImgError } from '@/utils/offlineMediaPlaceholder';

const props = withDefaults(
  defineProps<{
    heading?: string | null;
    /** Expanded rich text HTML from API. */
    text?: string | null;
    image?: ApiImage | string | null;
    /** First main content block: treat image as LCP when no page hero image. */
    lcpImage?: boolean;
  }>(),
  { lcpImage: false }
);

const imageResolved = computed((): ApiImage | null => {
  const raw = props.image;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return null;
    try {
      const parsed = JSON.parse(s) as unknown;
      if (parsed && typeof parsed === 'object') return parsed as ApiImage;
    } catch {
      return null;
    }
  }
  return raw as ApiImage;
});

const bodyHtml = computed(() => (props.text ?? '').trim());
const headingText = computed(() => (props.heading ?? '').trim());

const richtextEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(richtextEl, () => bodyHtml.value);

function legacyImageUrl(img: ApiImage | null): string | null {
  return img && 'url' in img && typeof (img as { url?: string }).url === 'string'
    ? (img as { url: string }).url
    : null;
}

const showCard = computed(
  () =>
    headingText.value.length > 0 ||
    bodyHtml.value.length > 0 ||
    imageResolved.value != null
);

const hasImage = computed(() => imageResolved.value != null);
</script>

<template>
  <section v-if="showCard" class="image-and-text-card" aria-label="Featured content">
    <div
      class="image-and-text-card__grid"
      :class="{ 'image-and-text-card__grid--no-image': !hasImage }"
    >
      <div v-if="imageResolved" class="image-and-text-card__media">
        <picture v-if="isOptimisedImage(imageResolved)">
          <source
            v-for="source in imageResolved.sources"
            :key="source.media_type"
            :type="source.media_type"
            :srcset="buildSrcSet(source.src_set)"
          />
          <img
            :src="imageResolved.fallback!.url"
            :srcset="buildSrcSet(imageResolved.fallback!.src_set)"
            sizes="(min-width: 1024px) 40vw, 100vw"
            :alt="imageResolved.title || headingText || ''"
            class="image-and-text-card__img"
            :loading="lcpImage ? 'eager' : 'lazy'"
            :fetchpriority="lcpImage ? 'high' : undefined"
            decoding="async"
            @error="onOfflineMediaImgError"
          />
        </picture>
        <img
          v-else-if="legacyImageUrl(imageResolved)"
          :src="legacyImageUrl(imageResolved)!"
          :alt="headingText || 'Illustration'"
          class="image-and-text-card__img"
          :loading="lcpImage ? 'eager' : 'lazy'"
          :fetchpriority="lcpImage ? 'high' : undefined"
          decoding="async"
          @error="onOfflineMediaImgError"
        />
      </div>

      <div class="image-and-text-card__body">
        <h2 v-if="headingText" class="image-and-text-card__heading">
          {{ headingText }}
        </h2>
        <div
          v-if="bodyHtml"
          ref="richtextEl"
          class="image-and-text-card__richtext"
          v-html="bodyHtml"
        />
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Prototype About.tsx: bg-card rounded-lg border; grid 1 / lg:2; image h-32 lg:h-auto object-cover;
   text p-4 sm:p-5 lg:p-6; h2 text-lg sm:text-xl font-semibold mb-2 sm:mb-3;
   body text-muted-foreground text-xs sm:text-sm leading-relaxed space-y-2 */
.image-and-text-card {
  width: 100%;
  background: var(--color-card-bg, #ffffff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 0.5rem;
  overflow: hidden;
}

.image-and-text-card__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

@media (min-width: 1024px) {
  .image-and-text-card__grid {
    grid-template-columns: 1fr 1fr;
  }

  .image-and-text-card__grid--no-image {
    grid-template-columns: 1fr;
  }
}

.image-and-text-card__media {
  height: 8rem;
  overflow: hidden;
  background: var(--color-border, #e5e7eb);
}

@media (min-width: 1024px) {
  .image-and-text-card__media {
    height: auto;
    min-height: 100%;
    align-self: stretch;
  }
}

.image-and-text-card__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-and-text-card__body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
}

@media (min-width: 640px) {
  .image-and-text-card__body {
    padding: 1.25rem;
  }
}

@media (min-width: 1024px) {
  .image-and-text-card__body {
    padding: 1.5rem;
  }
}

.image-and-text-card__heading {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-text, #111827);
}

@media (min-width: 640px) {
  .image-and-text-card__heading {
    margin-bottom: 0.75rem;
    font-size: 1.25rem;
  }
}

.image-and-text-card__richtext {
  display: flow-root;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.625;
  color: var(--color-text-muted, #6b7280);
}

@media (min-width: 640px) {
  .image-and-text-card__richtext {
    font-size: 0.875rem;
  }
}

.image-and-text-card__richtext :deep(p) {
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

.image-and-text-card__richtext :deep(p + p) {
  margin-top: 0.5rem;
}

.image-and-text-card__richtext :deep(ul),
.image-and-text-card__richtext :deep(ol) {
  margin: 0.5rem 0 0;
  padding-left: 1.25em;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
}

.image-and-text-card__richtext :deep(li) {
  margin: 0.125rem 0;
}

.image-and-text-card__richtext :deep(a) {
  color: var(--color-link, #0066cc);
}

.image-and-text-card__richtext :deep(strong) {
  font-weight: 600;
  color: var(--color-text, #111827);
}

.image-and-text-card__richtext :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.2em;
  color: var(--color-primary);
}

.image-and-text-card__richtext :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}

.image-and-text-card__richtext :deep(.indmo-richtext-primary) {
  color: var(--color-primary);
}
</style>
